/**
 * PHARMAKON — shell navigation state (WP-18, design/ux/01_foundations.md §6.3,
 * §6.4). Nothing persisted.
 *
 *  · documentLabel — the document a detail page shows (« FA-2026-0142 »):
 *    the breadcrumb ends with it and the window title starts with it
 *    (`<PageHeader documentLabel>` publishes it). Stored with the path it
 *    belongs to, so a label never outlives its page (no reset race between
 *    the page's effect and the shell's).
 *  · titleOverride — a page that is not a menu page (NotFound) names itself,
 *    also per path.
 *  · flyoutRequest — the breadcrumb's category crumb asks the rail to open
 *    that category's flyout (a sequence so the same request can repeat).
 *  · badges — counts shown next to a menu item (`node.badge` key), published
 *    by the module that owns them (`setBadge`), never polled by the shell.
 */
import { create } from "zustand";

export const useNavStore = create((set) => ({
  documentLabel: null,
  titleOverride: null,
  flyoutRequest: null,
  badges: {},
  /** @param {string} path  location.pathname of the page  @param {string | null} label */
  setDocumentLabel: (path, label) =>
    set({ documentLabel: label != null && String(label).trim() !== "" ? { path, label: String(label) } : null }),
  setTitleOverride: (path, title) => set({ titleOverride: title ? { path, title: String(title) } : null }),
  requestFlyout: (categoryId) =>
    set((s) => ({ flyoutRequest: { categoryId, seq: (s.flyoutRequest?.seq ?? 0) + 1 } })),
  /**
   * @param {string} key   the node's `badge` key
   * @param {{ count: number, label: string } | null} value  `label` is the
   *   accessible name (« 3 opérations à confirmer »); null or 0 hides it
   */
  setBadge: (key, value) =>
    set((s) => {
      const badges = { ...s.badges };
      if (!value || !(Number(value.count) > 0)) delete badges[key];
      else badges[key] = { count: Number(value.count), label: String(value.label ?? value.count) };
      return { badges };
    }),
}));

export default useNavStore;

/** The value a per-path entry holds for `pathname`, else null. */
export const forPath = (entry, pathname) => (entry && entry.path === pathname ? entry : null);
